/**
 * Phase 18.3 — Premium notification sounds.
 *
 * Two short, classy tones generated entirely with the Web Audio API so
 * we ship ZERO MB of audio assets:
 *   • messageChime — soft two-note bell (≈ 0.45s)
 *   • tipChime     — golden triumphant trill (≈ 0.7s)
 *
 * Respects the user's "Reduce motion / sound" preference and a
 * localStorage opt-out (`nymia_sound_muted=1`).
 */

let _ctx = null;

const getCtx = () => {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
  } catch (_e) {
    _ctx = null;
  }
  return _ctx;
};

const isMuted = () => {
  try {
    if (localStorage.getItem('nymia_sound_muted') === '1') return true;
  } catch (_e) { /* ignore */ }
  if (typeof window === 'undefined') return true;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
};

const tone = (ctx, freq, startAt, dur, gain = 0.18) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  // ADSR envelope so the chime feels organic, not square-wavey.
  g.gain.setValueAtTime(0.0001, startAt);
  g.gain.exponentialRampToValueAtTime(gain, startAt + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
};

export const messageChime = () => {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  // Some browsers suspend the context until a user gesture — try to resume.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + 0.01;
  tone(ctx, 880, t0, 0.18, 0.14);          // A5
  tone(ctx, 1318.5, t0 + 0.11, 0.24, 0.12); // E6
};

// Giu 2026 — host bell for a new co-host request (local WebAudio, no assets).
export const cohostBell = () => {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + 0.01;
  // Two-strike bell: G5 → D6, then a softer echo.
  tone(ctx, 784.0, t0, 0.30, 0.18);
  tone(ctx, 1174.7, t0 + 0.18, 0.36, 0.16);
  tone(ctx, 1174.7, t0 + 0.62, 0.30, 0.09);
};

export const tipChime = () => {
  if (isMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + 0.01;
  // Golden trill: C6 → E6 → G6 → C7 staccato.
  tone(ctx, 1046.5, t0,        0.16, 0.16);
  tone(ctx, 1318.5, t0 + 0.10, 0.16, 0.16);
  tone(ctx, 1568.0, t0 + 0.20, 0.18, 0.18);
  tone(ctx, 2093.0, t0 + 0.32, 0.30, 0.22);
};

export const setMuted = (muted) => {
  try {
    if (muted) localStorage.setItem('nymia_sound_muted', '1');
    else localStorage.removeItem('nymia_sound_muted');
  } catch (_e) { /* ignore */ }
};

export const getMuted = () => isMuted();

// ─── O2O call tones (Giu 2026) ────────────────────────────────────────────
// Ring tones ignore the reduced-motion preference: a call must be audible.
const explicitlyMuted = () => {
  try { return localStorage.getItem('nymia_sound_muted') === '1'; } catch (_e) { return false; }
};

/** Resume the shared AudioContext on the first user gesture (autoplay policy). */
export const primeAudio = () => {
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  };
  document.addEventListener('pointerdown', handler, { capture: true, passive: true });
  document.addEventListener('keydown', handler, { capture: true });
  return () => {
    document.removeEventListener('pointerdown', handler, { capture: true });
    document.removeEventListener('keydown', handler, { capture: true });
  };
};

const loopPattern = (play, periodMs) => {
  if (explicitlyMuted()) return () => {};
  const ctx = getCtx();
  if (!ctx) return () => {};
  const cycle = () => {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    if (ctx.state !== 'running') return;
    play(ctx, ctx.currentTime + 0.02);
  };
  cycle();
  const id = setInterval(cycle, periodMs);
  return () => clearInterval(id);
};

/** Caller side — Italian ringback: 425 Hz, 1 s on / 4 s off. */
export const startRingback = () => loopPattern((ctx, t0) => {
  tone(ctx, 425, t0, 1.0, 0.16);
}, 5000);

/** Callee side — two double-rings per cycle + vibration on Android. */
export const startRingtone = () => {
  const stopTone = loopPattern((ctx, t0) => {
    [0, 0.45, 1.3, 1.75].forEach((off) => {
      tone(ctx, 1046.5, t0 + off, 0.22, 0.2);
      tone(ctx, 1318.5, t0 + off + 0.11, 0.22, 0.18);
    });
  }, 3200);
  const vib = () => { try { navigator.vibrate?.([400, 200, 400, 1200]); } catch (_e) { /* ignore */ } };
  vib();
  const vibId = setInterval(vib, 3200);
  return () => { stopTone(); clearInterval(vibId); try { navigator.vibrate?.(0); } catch (_e) { /* ignore */ } };
};
